/**
 * EntryRefinementCard — the typed card for `propose_entry_refinement`
 * (ChatMessage.TOOL_CARDS; backend allowlist agent_types.RENDERED_TOOLS).
 *
 * Renders Arc's quote-verified refinement proposal as a field-by-field diff
 * (current → proposed) with per-field checkboxes. Apply goes through the
 * drawer's context handler → the deterministic /refine/apply endpoint — Arc
 * never writes the card. Outside a refine drawer (e.g. re-reading the
 * conversation from the main chat page) the card renders read-only.
 */
import React, { useMemo, useState } from 'react';

import { useChronologyRefine } from './chronologyRefineContext';

const S = {
  card: { marginTop: 8, border: '1px solid #d9cdf5', borderRadius: 8, background: '#faf8ff', overflow: 'hidden', fontSize: 13 },
  head: { padding: '8px 12px', background: '#efe8fd', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
  body: { padding: '8px 12px' },
  row: { display: 'flex', gap: 8, padding: '7px 0', borderTop: '1px solid #eee6fb', alignItems: 'flex-start' },
  fieldName: { fontWeight: 600, width: 96, flexShrink: 0, textTransform: 'capitalize', paddingTop: 1 },
  current: { color: '#889', textDecoration: 'line-through', whiteSpace: 'pre-wrap' },
  proposed: { color: '#223', whiteSpace: 'pre-wrap' },
  arrow: { color: '#8a7fae', padding: '0 2px' },
  quote: { fontSize: 12, color: '#556', fontStyle: 'italic', marginTop: 3 },
  rationale: { fontSize: 12, color: '#667', padding: '6px 0 2px' },
  actions: { display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid #eee6fb', background: '#fdfcff' },
  btnPrimary: { padding: '5px 12px', borderRadius: 6, border: '1px solid #5b3fa8', background: '#5b3fa8', color: '#fff', cursor: 'pointer', fontSize: 13 },
  hint: { fontSize: 12, color: '#8a7fae' },
  applied: { fontSize: 13, color: '#2c6e49', fontWeight: 600 },
};

function truncate(s, n = 400) {
  const str = String(s ?? '');
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
}

const FIELD_LABELS = {
  fact_adjudication: 'correction',
  date_candidate: 'suggested date',
  enrichment_patch: 'extra notes',
};

function DiffRow({ field, checked, onToggle, current, proposed, canApply, children }) {
  return (
    <div style={S.row}>
      {canApply && (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          title={`Include ${field} in the apply`}
          style={{ marginTop: 2 }}
        />
      )}
      <span style={S.fieldName}>{FIELD_LABELS[field] || field.replace(/_/g, ' ')}</span>
      <div style={{ flex: 1 }}>
        {current != null && current !== '' && (
          <span style={S.current}>{truncate(current)}</span>
        )}
        {current != null && current !== '' && <span style={S.arrow}> → </span>}
        <span style={S.proposed}>{truncate(proposed)}</span>
        {children}
      </div>
    </div>
  );
}

export default function EntryRefinementCard({ result }) {
  const refine = useChronologyRefine();
  const proposal = result?.proposal;
  const current = result?.current || {};
  const proposalKey = useMemo(
    () => JSON.stringify(proposal || {}),
    [proposal],
  );
  const [checked, setChecked] = useState(() => {
    const init = {};
    for (const k of Object.keys(proposal || {})) {
      if (k !== 'change_rationale') init[k] = true;
    }
    return init;
  });
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  if (!proposal) return null;

  // The card can only apply to the entry it was proposed for; a stale drawer
  // (different entry) renders it read-only rather than mis-applying.
  const canApply = !!refine && refine.entryId === current.entry_id;
  const appliedIso = refine?.appliedAt?.[proposalKey];
  const toggle = (k) => setChecked((c) => ({ ...c, [k]: !c[k] }));

  const fields = [];
  if (proposal.title != null) {
    fields.push(['title', current.title, proposal.title, null]);
  }
  if (proposal.description != null) {
    fields.push(['description', current.description, proposal.description, null]);
  }
  if (proposal.note != null) {
    fields.push(['note', current.note, proposal.note, null]);
  }
  if (proposal.participants != null) {
    const cur = (current.participants || [])
      .map((p) => (typeof p === 'object' ? (p.canonical_name || p.name || '') : String(p)))
      .filter(Boolean).join(', ');
    fields.push(['participants', cur, (proposal.participants || []).join(', '), null]);
  }

  const cand = proposal.date_candidate;
  const patch = proposal.enrichment_patch;
  const adjudication = proposal.fact_adjudication;

  const apply = async () => {
    if (!canApply || applying) return;
    const payload = {};
    for (const [k] of fields) {
      if (checked[k]) payload[k] = proposal[k];
    }
    if (cand && checked.date_candidate) payload.date_candidate = cand;
    if (patch && checked.enrichment_patch) payload.enrichment_patch = patch;
    if (adjudication && checked.fact_adjudication) payload.fact_adjudication = adjudication;
    if (!Object.keys(payload).length) {
      setError('Nothing selected to apply.');
      return;
    }
    payload.change_rationale = proposal.change_rationale || '';
    setApplying(true); setError('');
    try {
      await refine.apply(payload, proposalKey);
    } catch (e) {
      setError(e.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div style={S.card}>
      <div style={S.head}>
        <span>✨ Proposed refinement</span>
        {result.dropped_quotes > 0 && (
          <span style={S.hint}>({result.dropped_quotes} unverified quote{result.dropped_quotes === 1 ? '' : 's'} dropped)</span>
        )}
      </div>
      <div style={S.body}>
        {proposal.change_rationale && (
          <div style={S.rationale}>{proposal.change_rationale}</div>
        )}
        {fields.map(([k, cur, prop]) => (
          <DiffRow
            key={k} field={k} current={cur} proposed={prop}
            checked={!!checked[k]} onToggle={() => toggle(k)} canApply={canApply}
          />
        ))}
        {cand && (
          <DiffRow
            field="date_candidate"
            current={current.effective_date ? String(current.effective_date).slice(0, 10) : 'Undated'}
            proposed={`${String(cand.date).slice(0, 10)} (${cand.confidence || 'medium'} — becomes a pick-able candidate on the card)`}
            checked={!!checked.date_candidate} onToggle={() => toggle('date_candidate')} canApply={canApply}
          >
            {cand.rationale && <div style={S.rationale}>{cand.rationale}</div>}
            {cand.quote && <div style={S.quote}>“{cand.quote}”</div>}
          </DiffRow>
        )}
        {adjudication && (
          <DiffRow
            field="fact_adjudication"
            current="(the system's original reading)"
            proposed={adjudication.assertion}
            checked={!!checked.fact_adjudication} onToggle={() => toggle('fact_adjudication')} canApply={canApply}
          >
            <div style={S.rationale}>
              ⚖️ Saved as your correction — every screen (people, timeline,
              future Arc sessions) will show it from now on. The system's
              original readings stay on record underneath.
            </div>
            {(adjudication.role_rulings || []).length > 0 && (
              <div style={S.rationale}>
                {(adjudication.role_rulings || []).map((r, i) => (
                  <span key={i} style={{ marginRight: 10 }}>
                    <b>{r.name}</b> → {String(r.role_bucket || '').toLowerCase()}
                  </span>
                ))}
              </div>
            )}
            {(adjudication.evidence || []).map((q, i) => (
              <div key={i} style={S.quote}>“{q.quote}”</div>
            ))}
          </DiffRow>
        )}
        {patch && (
          <DiffRow
            field="enrichment_patch"
            current={current.enrichment_summary}
            proposed={patch.summary || `${(patch.key_quotes || []).length} quote(s), ${(patch.date_observations || []).length} date note(s)`}
            checked={!!checked.enrichment_patch} onToggle={() => toggle('enrichment_patch')} canApply={canApply}
          >
            {(patch.key_quotes || []).map((q, i) => (
              <div key={i} style={S.quote}>“{q.text}”</div>
            ))}
          </DiffRow>
        )}
      </div>
      <div style={S.actions}>
        {appliedIso ? (
          <span style={S.applied}>✓ Applied to the card</span>
        ) : canApply ? (
          <>
            <button style={S.btnPrimary} onClick={apply} disabled={applying || !!refine.busy}>
              {applying ? 'Applying…' : 'Apply to the card'}
            </button>
            <span style={S.hint}>Uncheck anything you don’t want. Applying archives Arc’s notes on this card.</span>
          </>
        ) : (
          <span style={S.hint}>Open Refine with Arc from this entry’s card on the Chronology tab to apply.</span>
        )}
        {error && <span style={{ color: '#c0392b', fontSize: 12 }}>{error}</span>}
      </div>
    </div>
  );
}
